// "New dictionary" (docs/project.md §7): choice of the target language, the native one prefilled.
import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { api } from '@/api/endpoints';
import { ApiRequestError } from '@/api/client';
import { refreshDictionaries, useDictionaries, useUser } from '@/state/session';
import { storage } from '@/lib/storage';
import { LanguagePicker, LanguageRow } from '@/components/LanguagePicker';
import { ErrorText, Group, Hint, PrimaryButton, Sheet, SheetBar, useCloseOverlay } from '@/components/ui';
import { reportError } from '@/lib/errors';
import { track } from '@/lib/analytics';

export const Route = createFileRoute('/_app/dictionaries/new')({
  component: NewDictionary,
});

function NewDictionary() {
  const t = useTranslations('newDictionary');
  const tc = useTranslations('common');
  const navigate = useNavigate();
  const user = useUser();
  const dictionaries = useDictionaries();
  const [baseLang, setBaseLang] = useState(user?.nativeLang ?? 'en');
  const [targetLang, setTargetLang] = useState<string | null>(null);
  const [picker, setPicker] = useState<'native' | 'target' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = useCloseOverlay({ to: '/' });

  const create = async () => {
    if (!targetLang) return;
    if (dictionaries.some((d) => d.role === 'owner' && d.targetLang === targetLang && d.baseLang === baseLang)) {
      setError(t('exists'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.dictionaries.create({ targetLang, baseLang });
      track('dictionary_created', { target_lang: targetLang, base_lang: baseLang });
      storage.setLastDictionary(res.dictionary.id);
      await refreshDictionaries();
      void navigate({ to: '/d/$dictionaryId', params: { dictionaryId: res.dictionary.id }, replace: true });
    } catch (e) {
      reportError(e);
      setError(e instanceof ApiRequestError && e.code === 'conflict' ? t('exists') : tc('errorGeneric'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet onClose={close} label={t('title')}>
      <SheetBar onClose={dictionaries.length ? close : undefined} left={dictionaries.length ? undefined : <div className="h-11 w-11" />} title={t('title')} />
      <form
        className="flex flex-col gap-5 px-4 pb-8 pt-3"
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <Hint>{t('subtitle')}</Hint>
        <Group className="!rounded-field">
          <LanguageRow label={t('targetLang')} value={targetLang} onClick={() => setPicker('target')} />
          <LanguageRow label={t('nativeLang')} value={baseLang} onClick={() => setPicker('native')} />
        </Group>
        <PrimaryButton busy={busy} disabled={!targetLang} data-testid="create-dictionary">
          {t('create')}
        </PrimaryButton>
        <ErrorText>{error}</ErrorText>
      </form>
      {picker && (
        <LanguagePicker
          title={picker === 'native' ? t('nativeLang') : t('targetLang')}
          value={picker === 'native' ? baseLang : targetLang}
          exclude={picker === 'target' ? [baseLang] : targetLang ? [targetLang] : []}
          onSelect={(code) => {
            if (picker === 'native') setBaseLang(code);
            else setTargetLang(code);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </Sheet>
  );
}
